/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import isString from 'lodash.isstring';
import Device from '@ion/core/src/services/Device';
import {GenericObject, ToolAssetType} from '@ion/core/src/types/entities';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import {TagsAccordion as CurrentTagsAccordion} from '@ion/dls/src/components/assetdetails/current/TagsAccordion';
import {TagsTabKey} from '@ion/dls/src/components/assetdetails/current/TagsCoachingText';
import {TagTitleRowData} from '@ion/dls/src/components/assetdetails/current/TagTitleList';
import {
  TAG_ASSET_TYPE_ICONS,
  TagAssetType
} from '@ion/dls/src/components/assetdetails/current/TagTitleRow';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {getSelectedAssetTools} from '../../store/actions/tracking';
import {isToolsLoadingSelector} from '../../store/selectors/tracking-selectors';
import {parseAssociatedAsset} from './AssetCardListContent';
import {
  AssetDetailsSection,
  useRegisteredAccordion
} from './assetDetailsAccordionStore';
import {openToolDetail} from './toolDetailActions';

export interface TagsAccordionContainerProps {
  asset: Device;
  onSelectAssociatedAsset?: (e: MouseEvent, associatedAsset: GenericObject) => void;
}

/**
 * Picks the tags the In Range tab lists - the assigned tags currently in
 * range. There is no separate source of nearby tags today, so the tab used to
 * stay empty while the Assigned tab showed some of the same tags as In Range;
 * the two tabs now always agree.
 *
 * ```typescript
 * import {getInRangeRows} from './TagsAccordion';
 *
 * ...
 *
 * const nearbyRows = getInRangeRows(assignedRows);
 * ```
 *
 * @param assignedRows The Assigned tab's rows.
 * @returns            The rows whose range is `in`.
 */
export const getInRangeRows = (assignedRows: TagTitleRowData[]): TagTitleRowData[] => (
  assignedRows.filter(row => row.range === 'in')
);

// the valid TagAssetType set, derived from TAG_ASSET_TYPE_ICONS's own keys
// (shared with ToolCard.tsx) rather than a separately hand-maintained list,
// so a type added there is automatically recognized here too ...
const TAG_ASSET_TYPES = Object.keys(TAG_ASSET_TYPE_ICONS) as TagAssetType[];

// maps a raw tool's `assetType` (e.g. 'Tool', 'Trailer' - matches AssetType
// enum values) onto the Tags accordion's own lowercase TagAssetType union,
// falling back to 'tool' for anything the accordion has no icon for ...
const toTagAssetType = (assetType?: string): TagAssetType => {
  const lowered = assetType?.toLowerCase() as TagAssetType;
  return TAG_ASSET_TYPES.includes(lowered) ? lowered : 'tool';
};

export interface MergeAssignedRowsOptions {
  parsedAssignedAssets: any[];
  assetTools: ToolAssetType[];
  originatingAssetId: string;
  onNameClick: (tool: ToolAssetType) => void;
  onSelectAssociatedAsset?: (e: MouseEvent, associatedAsset: GenericObject) => void;
  toolsLoading: boolean;
}

const NOOP_MOUSE_EVENT = {stopPropagation: () => {}} as MouseEvent;

/**
 * Merges `getSelectedAssetTools`'s tag-tools with the broader raw
 * `assignedAssets` list (parsed via `AssetCardListContent`'s exported
 * `parseAssociatedAsset`) into one deduped `TagTitleRowData[]`, keyed by
 * asset id so an iOn-Tag-equipped tool is never listed twice - the pure
 * merge/dedup pass behind `TagsAccordion`'s own `assignedRows`, extracted so
 * it's testable directly against fixtures without mounting the container.
 *
 * ```typescript
 *
 *   import {mergeAssignedRows} from '@/routes/tracking/TagsAccordion';
 *   ...
 *
 *   const rows = mergeAssignedRows({
 *     parsedAssignedAssets, assetTools, originatingAssetId: '123', onNameClick, toolsLoading: false
 *   });
 *
 * ```
 *
 * @param options.parsedAssignedAssets    the raw assigned-assets list, already
 *                                        parsed
 * @param options.assetTools              the selected asset's tag-equipped
 *                                        tools
 * @param options.originatingAssetId      the selected asset's own id
 * @param options.onNameClick             called with the clicked tool
 * @param options.onSelectAssociatedAsset called with the clicked plain
 *                                        assigned asset (no matching tool)
 * @param options.toolsLoading            `true` while `assetTools` is still
 *                                        loading - see the class doc's
 *                                        flip-avoidance note
 * @returns The deduped, merged row data
 */
export const mergeAssignedRows = ({
  parsedAssignedAssets,
  assetTools,
  originatingAssetId,
  onNameClick,
  onSelectAssociatedAsset,
  toolsLoading
}: MergeAssignedRowsOptions): TagTitleRowData[] => {
  const rowsById = new Map<string, TagTitleRowData>();

  // an assigned asset with no matching tool has no real range data, so it
  // defaults to 'in' - but that default is only trustworthy once
  // `getSelectedAssetTools` has actually resolved; rendering it while
  // still loading, then correcting to 'out' the moment a match arrives,
  // is what made rows visibly flip from in-range to out-of-range ...
  if (!toolsLoading) {
    (parsedAssignedAssets || []).forEach(function(rawAsset) {
      const parsedAsset = parseAssociatedAsset(originatingAssetId, rawAsset);
      rowsById.set(String(parsedAsset.id), {
        id          : String(parsedAsset.id),
        assetType   : toTagAssetType(parsedAsset.type),
        assetName   : parsedAsset.name,
        range       : 'in',
        onNameClick : function() {
          onSelectAssociatedAsset?.(NOOP_MOUSE_EVENT, parsedAsset);
        }
      });
    });
  }

  assetTools.forEach(function(tool: ToolAssetType) {
    rowsById.set(String(tool.id), {
      id          : String(tool.id),
      assetType   : toTagAssetType(tool.assetType),
      assetName   : tool.name,
      range       : tool.tagReport ? 'in' : 'out',
      onNameClick : function() {
        onNameClick(tool);
      }
    });
  });

  return Array.from(rowsById.values());
};

/**
 * TagsAccordion renders the ION-7463 "Tags" section of the Asset Details
 * Drawer (current theme only - legacy renders nothing here, since legacy
 * has no equivalent top-level Tags card; its own assigned-assets/contained-
 * tools rows live inline inside `AssetCardListContent`/`ContainedAsset`,
 * untouched by this component).
 *
 * The "Assigned" tab merges two data sources, keyed by asset id so an
 * iOn-Tag-equipped asset is never listed twice:
 * - `getSelectedAssetTools`'s tools, each carrying a `tagReport` (this is
 *   the same presence/absence signal `RangeStatusBadge`'s
 *   `range: 'in' | 'out'` represents - present means the tool is currently
 *   reporting/visible, i.e. "in range"). Clicking one of these rows opens
 *   its own detail view via `toolDetailActions.ts`'s shared `openToolDetail`,
 *   the same helper `ContainedAsset.tsx`'s own `showToolDetail` reuses for
 *   its core `setToolDetailView`/`setTool`/`setHoveredAsset` sequence, so
 *   "click a tag" behaves identically to the legacy contained-tools list.
 * - the broader `assignedAssets` (`asset.getDevice().assets`, parsed via
 *   `AssetCardListContent`'s exported `parseAssociatedAsset`) - every asset
 *   assigned to this one regardless of iOn-Tag equipment, per Figma shown
 *   with the same icon/name row as a tag but no tag-specific range data, so
 *   it defaults to `range: 'in'` (no "out of range"/"Last Known" line).
 *   Any entry here that's also in the tools list above is overridden by the
 *   richer tag data rather than shown twice. Clicking one of these rows
 *   calls `onSelectAssociatedAsset` - the same handler legacy's inline
 *   `AssetCardListContent` assigned-assets rows already call, so "click an
 *   asset" behaves identically to legacy (selects that asset and opens its
 *   own detail view).
 *
 * The "Nearby" tab always renders empty - no "tools physically near this
 * asset" data source exists anywhere in the app today (confirmed by
 * search); it stays that way until a real query for it exists.
 *
 * ```typescript
 *
 *   import {TagsAccordion} from '@/routes/tracking/TagsAccordion';
 *   ...
 *
 *   <TagsAccordion asset={asset}
 *     onSelectAssociatedAsset={onSelectAssociatedAsset} />
 *
 * ```
 *
 */
export const TagsAccordion = ({asset, onSelectAssociatedAsset}: TagsAccordionContainerProps) => {
  const dispatch                  = useDispatch();
  const [activeTab, setActiveTab] = useState<TagsTabKey>('assigned');
  const selectedAssetTools        = useSelector(({tracking}) => tracking.assets.selectedAssetTools);
  const {toolsLoading}            = useSelector(isToolsLoadingSelector);

  useEffect(() => {
    if (isCurrentTheme()) {
      dispatch(getSelectedAssetTools(asset?.getDevice?.(), true) as any);
    }
  }, [asset]);

  const assetTools = useMemo(() => (
    selectedAssetTools?.find(toolsAsset => asset?.id() === toolsAsset.assetId)?.tools || []
  ), [selectedAssetTools, asset]);

  const assignedAssetsRaw = asset?.getDevice?.()?.assets;
  const parsedAssignedAssets = useMemo(() => (
    isString(assignedAssetsRaw) ? safeJSONParse(assignedAssetsRaw) : assignedAssetsRaw
  ), [assignedAssetsRaw]);

  const onNameClick = useCallback(function(tool: ToolAssetType) {
    openToolDetail(dispatch, tool, asset);
  }, [asset, dispatch]);

  const assignedRows: TagTitleRowData[] = useMemo(() => (
    mergeAssignedRows({
      parsedAssignedAssets,
      assetTools,
      originatingAssetId : String(asset?.id()),
      onNameClick,
      onSelectAssociatedAsset,
      toolsLoading
    })
  ), [parsedAssignedAssets, assetTools, asset, onNameClick, onSelectAssociatedAsset, toolsLoading]);

  const nearbyRows = useMemo(() => getInRangeRows(assignedRows), [assignedRows]);

  // registers this section with the Asset Details Drawer's shared accordion
  // store so the drawer header's expand/collapse-all control can drive it -
  // current theme only ...
  const {
    expanded : isTagsExpanded,
    onExpand : onTagsExpand
  } = useRegisteredAccordion(AssetDetailsSection.tags, false, isCurrentTheme());

  return isCurrentTheme() ? (
    <CurrentTagsAccordion activeTab={activeTab}
      onTabChange={setActiveTab}
      assignedRows={assignedRows}
      nearbyRows={nearbyRows}
      expanded={isTagsExpanded}
      onExpand={onTagsExpand} />
  ) : null;
};

export default memo(TagsAccordion);
